/**
 * County choropleth color scales (share of total matched records or flags).
 * Shared by EnhancedMap styling and Map Layers legend.
 */

export const COUNTY_COLOR_SCALE = [
  { maxPct: 0, label: 'No data', color: '#ffffff' },
  { maxPct: 0.01, label: '<1%', color: '#90caf9' },
  { maxPct: 0.05, label: '1–5%', color: '#64b5f6' },
  { maxPct: 0.1, label: '5–10%', color: '#42a5f5' },
  { maxPct: 0.2, label: '10–20%', color: '#2196f3' },
  { maxPct: 0.35, label: '20–35%', color: '#1976d2' },
  { maxPct: 0.5, label: '35–50%', color: '#1565c0' },
  { maxPct: 0.7, label: '50–70%', color: '#0d47a1' },
  { maxPct: Infinity, label: '≥70%', color: '#0a3d7a' },
];

/** Red shade scale for Flag View (share of total flags) */
export const COUNTY_FLAG_COLOR_SCALE = [
  { maxPct: 0, label: 'No flags', color: '#ffffff' },
  { maxPct: 0.01, label: '<1%', color: '#ffcdd2' },
  { maxPct: 0.05, label: '1–5%', color: '#ef9a9a' },
  { maxPct: 0.1, label: '5–10%', color: '#e57373' },
  { maxPct: 0.2, label: '10–20%', color: '#ef5350' },
  { maxPct: 0.35, label: '20–35%', color: '#f44336' },
  { maxPct: 0.5, label: '35–50%', color: '#e53935' },
  { maxPct: 0.7, label: '50–70%', color: '#c62828' },
  { maxPct: Infinity, label: '≥70%', color: '#b71c1c' },
];

/**
 * Blue shade for lease diamonds (no flags / default view).
 * Scaled by share of max metric (typically well count) in the result set.
 */
export function getLeaseBlueColor(value, maxValue) {
  const t = maxValue > 0 ? Math.min(1, Math.max(0, Number(value) / maxValue)) : 0;
  if (t < 0.15) return '#90caf9';
  if (t < 0.3) return '#64b5f6';
  if (t < 0.45) return '#42a5f5';
  if (t < 0.6) return '#2196f3';
  if (t < 0.8) return '#1976d2';
  return '#0d47a1';
}

/**
 * Fill color for a lease diamond in Flag View.
 * 0 flags → success green (report card); otherwise red shaded by share of max flags.
 */
export function getLeaseFlagColor(flagCount, maxFlags) {
  if (!flagCount || flagCount <= 0) {
    return '#61ce70';
  }
  const t = maxFlags > 0 ? Math.min(1, flagCount / maxFlags) : 1;
  if (t < 0.15) return '#ef9a9a';
  if (t < 0.3) return '#e57373';
  if (t < 0.45) return '#ef5350';
  if (t < 0.6) return '#f44336';
  if (t < 0.8) return '#e53935';
  return '#c62828';
}

function colorFromShare(percentage, scale) {
  for (let i = 1; i < scale.length; i++) {
    if (percentage < scale[i].maxPct) {
      return scale[i].color;
    }
  }
  return scale[scale.length - 1].color;
}

export function getCountyColor(recordCount, totalRecords) {
  if (recordCount === 0) return '#ffffff';
  const percentage = totalRecords > 0 ? recordCount / totalRecords : 0;
  return colorFromShare(percentage, COUNTY_COLOR_SCALE);
}

export function getCountyOpacity(recordCount, totalRecords) {
  if (recordCount === 0) return 0.3;
  const percentage = totalRecords > 0 ? recordCount / totalRecords : 0;
  return 0.7 + (Math.min(percentage * 10, 1) * 0.2);
}

export function getCountyFlagColor(flagCount, totalFlags) {
  if (flagCount === 0) return '#ffffff';
  const percentage = totalFlags > 0 ? flagCount / totalFlags : 0;
  return colorFromShare(percentage, COUNTY_FLAG_COLOR_SCALE);
}

export function getCountyFlagOpacity(flagCount, totalFlags) {
  if (flagCount === 0) return 0.3;
  const percentage = totalFlags > 0 ? flagCount / totalFlags : 0;
  return 0.7 + (Math.min(percentage * 10, 1) * 0.2);
}
